﻿import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  ALL_PLAYER_RELEASES_QUERY_KEY,
  PLAYER_RELEASE_API_URL,
  PLAYER_RELEASE_BY_ID_QUERY_KEY
} from '@/constants';
import { apiUrl, getQueryParams } from '@/helpers';
import { ErrorResponse, Options } from '@/types';
import { CreateEditPlayerReleaseProps } from '@/types/PlayerReleaseTypes';

export const PlayerReleaseEndpoints = {
  getAllPlayerReleases: (pageSize: number, cursor: number, isPublic?: boolean) => {
    return axios.get(
      `${apiUrl()}${PLAYER_RELEASE_API_URL}${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'filter.IsPublic': isPublic as boolean
      })}`
    );
  },
  getPlayerReleaseById: (id: number) => axios.get(`${apiUrl()}${PLAYER_RELEASE_API_URL}/${id}`),
  updatePlayerRelease: (payload: CreateEditPlayerReleaseProps) =>
    axios.put(`${apiUrl()}${PLAYER_RELEASE_API_URL}`, payload),
  createPlayerRelease: (payload: CreateEditPlayerReleaseProps) =>
    axios.post(`${apiUrl()}${PLAYER_RELEASE_API_URL}`, payload),
  deletePlayerRelease: (id: number) => axios.delete(`${apiUrl()}${PLAYER_RELEASE_API_URL}/${id}`),
  publishPlayerReleas: (id: number) =>
    axios.patch(`${apiUrl()}${PLAYER_RELEASE_API_URL}/publish/${id}`)
};

export const useGetAllPlayerReleases = (pageSize: number, isPublic?: boolean) => {
  return useInfiniteQuery(
    [ALL_PLAYER_RELEASES_QUERY_KEY, { pageSize, isPublic }],
    ({ pageParam }) => PlayerReleaseEndpoints.getAllPlayerReleases(pageSize, pageParam, isPublic),
    {
      keepPreviousData: true,
      enabled: true,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      }
    }
  );
};

export const useGetPlayerReleaseByIdQuery = (id: number, options?: Options) => {
  return useQuery(
    [PLAYER_RELEASE_BY_ID_QUERY_KEY, { id }],
    () => PlayerReleaseEndpoints.getPlayerReleaseById(id),
    {
      enabled: !!id,
      retry: false,
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useUpdatePlayerReleaseMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(PlayerReleaseEndpoints.updatePlayerRelease, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_PLAYER_RELEASES_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useCreatePlayerReleaseMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(PlayerReleaseEndpoints.createPlayerRelease, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_PLAYER_RELEASES_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useDeletePlayerReleaseMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(PlayerReleaseEndpoints.deletePlayerRelease, {
    onSuccess: () => {
      options?.onSuccess();
      queryClient.invalidateQueries([ALL_PLAYER_RELEASES_QUERY_KEY]);
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const usePublishPlayerRelease = (queryClient: QueryClient, options: Options) => {
  return useMutation(PlayerReleaseEndpoints.publishPlayerReleas, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_PLAYER_RELEASES_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
